import React from "react";
import { createRoot } from "react-dom/client";
import App from "./App.tsx";
import { ErrorBoundary } from "./components/ErrorBoundary";
import {
  clearChunkReloadAttempt,
  recoverFromChunkLoadError,
} from "./lib/chunkLoadRecovery";
import { rememberPendingHelpLink } from "./lib/help/helpLinks";
// Fraunces servie par l'appli elle-même : plus d'appel à Google Fonts, qui recevait
// l'adresse IP de chaque visiteur avant tout consentement.
import "@fontsource-variable/fraunces/opsz.css";
import "@fontsource-variable/fraunces/opsz-italic.css";
import "./index.css";
import "react-day-picker/style.css";

if (typeof window !== "undefined") {
  // Un lien d'aide (`?aide=`) doit survivre à la connexion, qui renvoie toujours vers « Suivi ».
  rememberPendingHelpLink(window.location.pathname, window.location.search);

  window.addEventListener("load", () => {
    clearChunkReloadAttempt();
  });

  window.addEventListener("error", (event) => {
    recoverFromChunkLoadError(event.error ?? event.message);
  });

  window.addEventListener("unhandledrejection", (event) => {
    recoverFromChunkLoadError(event.reason);
  });
}

const root = createRoot(document.getElementById("root")!);
root.render(
  <React.StrictMode>
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  </React.StrictMode>
);

// Register service worker for PWA offline support
if (import.meta.env.PROD) {
  import("virtual:pwa-register").then(({ registerSW }) => {
    registerSW({ immediate: true });
  });
}

// Defer Sentry init until after first paint to prioritize LCP
if (import.meta.env.PROD) {
  import("./lib/sentry").then(({ ensureSentryInit }) => {
    if ("requestIdleCallback" in window) {
      (window as Window).requestIdleCallback(() => ensureSentryInit(), { timeout: 3000 });
    } else {
      setTimeout(ensureSentryInit, 1000);
    }
  });
}

// Defer PostHog init to avoid impacting initial rendering. Without a valid consent it does
// nothing (no cookie, no request); it must still run in all environments so that returning
// users who accepted keep being tracked without having to re-accept the banner.
import("./lib/posthog").then(({ ensurePosthogInit }) => {
  if ("requestIdleCallback" in window) {
    (window as Window).requestIdleCallback(() => ensurePosthogInit(), { timeout: 3000 });
  } else {
    setTimeout(ensurePosthogInit, 1000);
  }
});
